import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/ui/wf/common/button/Button';
import { PhotoManager } from './hooks';
import clsx from 'clsx';

interface PhotoGridProps {
  photoManager: PhotoManager;
  themeColor: 'blue' | 'green';
}

const themeClasses = {
  blue: {
    border: 'border-blue-300',
    rowBorder: 'border-blue-200',
    text: 'text-blue-700',
    buttonBg: 'bg-blue-500',
    buttonHover: 'hover:bg-blue-600',
  },
  green: {
    border: 'border-green-300',
    rowBorder: 'border-green-200',
    text: 'text-green-700',
    buttonBg: 'bg-green-600',
    buttonHover: 'hover:bg-green-700',
  },
};

const PhotoGrid = ({ photoManager, themeColor }: PhotoGridProps) => {
  const { t } = useTranslation();

  const {
    photoRows,
    checkedPhotos,
    addRow,
    handlePhotoChange,
    togglePhotoCheck,
    deleteSelectedPhotos,
    loadPhoto,
    loadingRows,
  } = photoManager;

  const classes = themeClasses[themeColor];

  const renderPhotoLabel = (id?: string) => {
    if (!id) return t('wf.statusInformationRegistration.photoRowLabel');
    return t('wf.statusInformationRegistration.photoRowLabelWithId', { id });
  };

  return (
    <div className={clsx('mt-4 space-y-4 border-t pt-4', classes.border)}>
      {photoRows.map((rowFiles, rowIndex) => {
        const isRowFull = rowFiles.length >= 15;
        const hasPhoto = rowFiles.length > 0;
        const photoLabel = renderPhotoLabel(rowFiles[0]?.id);
        const inputId = `photo-upload-${themeColor}-${rowIndex}`;
        return (
          <div
            key={`photo-row-${themeColor}-${rowIndex}`}
            className={clsx('space-y-2 border-b pb-4 last:border-0', classes.rowBorder)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={clsx('text-sm font-bold', classes.text)}>
                  <div className="flex items-center gap-2">
                    <span>{photoLabel}</span>
                    {!hasPhoto && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoChange(rowIndex, e)}
                          className="hidden"
                          id={inputId}
                          disabled={isRowFull}
                          data-testid={`photo-upload-input-${rowIndex}`}
                        />
                        <label
                          htmlFor={inputId}
                          className={`cursor-pointer rounded px-3 py-1 text-sm text-white ${classes.buttonBg} ${classes.buttonHover} ${rowFiles.length >= 15 ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          {t('wf.common.select')}
                        </label>
                      </>
                    )}
                  </div>
                  {!rowFiles[0]?.id && (
                    <span className="block font-normal text-black">
                      {t('wf.statusInformationRegistration.photoIdAssignedAfterRegistration')}
                    </span>
                  )}
                </div>
              </div>

              {(checkedPhotos[rowIndex]?.length ?? 0) > 0 && (
                <Button
                  title={`${t('wf.common.delete')} (${checkedPhotos[rowIndex]?.length})`}
                  onClick={() => deleteSelectedPhotos(rowIndex)}
                  className="bg-red-500 px-2 py-1 text-xs hover:bg-red-600"
                />
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              {rowFiles.map((photo, photoIndex) => {
                const isUnloaded = Boolean(photo.id) && !photo.url;
                const isLoading = loadingRows[rowIndex] ?? false;
                return (
                  <div key={photoIndex} className="group relative size-[300px] border bg-white">
                    <input
                      type="checkbox"
                      className="absolute left-1 top-1 z-10 size-4 cursor-pointer"
                      checked={checkedPhotos[rowIndex]?.includes(photoIndex) ?? false}
                      onChange={() => togglePhotoCheck(rowIndex, photoIndex)}
                    />
                    {isUnloaded ? (
                      <button
                        type="button"
                        onClick={() => loadPhoto(rowIndex)}
                        disabled={isLoading}
                        data-testid={`photo-placeholder-${rowIndex}`}
                        className="flex size-full flex-col items-center justify-center gap-1 text-xs text-gray-500 hover:bg-gray-50 disabled:cursor-wait"
                      >
                        <span>
                          {isLoading
                            ? t('wf.statusInformationRegistration.photoLoading')
                            : t('wf.statusInformationRegistration.photoTapToShow')}
                        </span>
                      </button>
                    ) : (
                      <img
                        src={photo.url}
                        alt={photo.name || `preview-${photoIndex}`}
                        className={`size-full object-cover ${checkedPhotos[rowIndex]?.includes(photoIndex) ? 'opacity-40' : ''}`}
                      />
                    )}
                    <div className="absolute bottom-0 w-full truncate bg-black/40 px-1 text-[8px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                      {photo.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="flex">
        <Button
          title="+"
          onClick={addRow}
          disabled={photoRows.length >= 15}
          className={clsx('px-[10px] py-1 text-2xl leading-none', classes.buttonBg, {
            'opacity-50': photoRows.length >= 15,
          })}
        />
      </div>
    </div>
  );
};

export default PhotoGrid;
